import { FC, SVGProps } from "react";
import { MenuItemOption } from "../../../common/components/functional/actionMenu/types";
import IconWrapper from "../../../common/components/functional/icons/IconWrapper";
import { ReactComponent as AssignToMeIcon } from "../../../common/icons/arrow-up-left.svg";
import { ReactComponent as MessageDoneIcon } from "../../../common/icons/messaging-done2.svg";
import { ReactComponent as MessageForwardIcon } from "../../../common/icons/messaging-forward.svg";
import { ToolbarAction } from "../types/common";

const MENU_ICON_SIZE = 16;
const TOOLBAR_ICON_SIZE = 22;

export type ConversationActionKey = "done" | "assign" | "forward";

export type ConversationActionAvailability = {
  canAssignConversation: boolean;
  canMarkAsDone: boolean;
  canTransferConversation: boolean;
  isClosed: boolean;
};

type ConversationActionHandlers = {
  assignToMe: () => void;
  markAsDone: () => void;
  onForward: () => void;
};

type SvgIconComponent = FC<SVGProps<SVGSVGElement>>;

type ConversationActionDef = {
  labelKey: string;
  closedLabelKey?: string;
  icon: SvgIconComponent;
  toolbarColor: string;
  menuColor: string;
  menuTextSx?: { color: string };
};

const ACTION_DEFS: Record<ConversationActionKey, ConversationActionDef> = {
  done: {
    labelKey: "markAsDone",
    icon: MessageDoneIcon,
    toolbarColor: "success.main",
    menuColor: "success.main",
    menuTextSx: { color: "success.main" },
  },
  assign: {
    labelKey: "assignToMe",
    closedLabelKey: "moveToInProgress",
    icon: AssignToMeIcon,
    toolbarColor: "text.secondary",
    menuColor: "text.primary",
  },
  forward: {
    labelKey: "forwardConversation",
    icon: MessageForwardIcon,
    toolbarColor: "text.secondary",
    menuColor: "text.primary",
  },
};

const PAGE_TOOLBAR_ACTIONS: ConversationActionKey[] = [
  "done",
  "assign",
  "forward",
];

const FLOATING_MENU_ACTIONS: ConversationActionKey[] = [
  "done",
  "assign",
  "forward",
];

const actionIcon = (
  icon: SvgIconComponent,
  fontSize: number,
  color: string,
) => (
  <IconWrapper
    icon={icon}
    sx={{
      fontSize,
      color,
      "& path": { fill: "currentColor", fillOpacity: 1 },
    }}
  />
);

export const getConversationActionLabelKey = (
  key: ConversationActionKey,
  availability: ConversationActionAvailability,
): string => {
  const def = ACTION_DEFS[key];
  if (key === "assign" && availability.isClosed && def.closedLabelKey) {
    return def.closedLabelKey;
  }
  return def.labelKey;
};

export const isConversationActionEnabled = (
  key: ConversationActionKey,
  availability: ConversationActionAvailability,
): boolean => {
  switch (key) {
    case "done":
      return availability.canMarkAsDone;
    case "assign":
      return availability.canAssignConversation;
    case "forward":
      return availability.canTransferConversation;
  }
};

const runConversationAction = (
  key: ConversationActionKey,
  handlers: ConversationActionHandlers,
) => {
  switch (key) {
    case "done":
      handlers.markAsDone();
      return;
    case "assign":
      handlers.assignToMe();
      return;
    case "forward":
      handlers.onForward();
      return;
  }
};

type ConversationActionsArgs = {
  t: (key: string) => string;
  availability: ConversationActionAvailability;
  handlers: ConversationActionHandlers;
};

export const getPageToolbarActions = ({
  t,
  availability,
  handlers,
}: ConversationActionsArgs): ToolbarAction[] =>
  PAGE_TOOLBAR_ACTIONS.map((key) => {
    const def = ACTION_DEFS[key];
    const disabled = !isConversationActionEnabled(key, availability);

    return {
      disabled,
      ariaLabel: t(getConversationActionLabelKey(key, availability)),
      onClick: () => runConversationAction(key, handlers),
      icon: actionIcon(
        def.icon,
        TOOLBAR_ICON_SIZE,
        disabled ? "text.disabled" : def.toolbarColor,
      ),
    };
  });

export const getConversationActionMenuItems = ({
  t,
  availability,
  handlers,
}: ConversationActionsArgs): MenuItemOption[] =>
  FLOATING_MENU_ACTIONS.filter((key) =>
    isConversationActionEnabled(key, availability),
  ).map((key) => {
    const def = ACTION_DEFS[key];

    return {
      key,
      label: t(getConversationActionLabelKey(key, availability)),
      textSx: def.menuTextSx,
      icon: actionIcon(def.icon, MENU_ICON_SIZE, def.menuColor),
      action: () => runConversationAction(key, handlers),
    };
  });
